import { useState, useEffect } from 'react'
import { subscribeToCompanyProspects, createCompanyProspect, deleteCompanyProspect, updateCompanyProspect } from '../firebase/companyProspects'
import { subscribeToTags } from '../firebase/tags'
import './CompanyProspectResearch.css'

const CompanyProspectResearch = ({ user }) => {
  const [companies, setCompanies] = useState([])
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingCompany, setEditingCompany] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTagFilter, setSelectedTagFilter] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, companyId: null, companyName: '' })
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    if (!user) return

    // Subscribe to real-time company prospects updates (shared resource)
    const unsubscribeCompanies = subscribeToCompanyProspects((companiesData) => {
      setCompanies(companiesData)
      setLoading(false)
    })

    // Subscribe to real-time tags updates (for tagging companies)
    const unsubscribeTags = subscribeToTags(user.uid, (tagsData) => {
      setTags(tagsData.map(tag => tag.name))
    })

    return () => {
      unsubscribeCompanies()
      unsubscribeTags()
    }
  }, [user])

  const handleCreateCompany = async (companyData) => {
    try {
      await createCompanyProspect(companyData)
      setShowForm(false)
      setEditingCompany(null)
    } catch (error) {
      console.error('Error creating company prospect:', error)
      alert('Failed to create company prospect. Please try again.')
    }
  }

  const handleUpdateCompany = async (companyData) => {
    try {
      await updateCompanyProspect(editingCompany.id, companyData)
      setShowForm(false)
      setEditingCompany(null)
    } catch (error) {
      console.error('Error updating company prospect:', error)
      alert('Failed to update company prospect. Please try again.')
    }
  }

  const handleEditCompany = (company) => {
    setEditingCompany(company)
    setShowForm(true)
  }

  const handleDeleteCompany = async (companyId, e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    
    const company = companies.find(c => c.id === companyId)
    const companyName = company?.name || 'this company'
    
    setDeleteConfirm({ show: true, companyId, companyName })
  }

  const confirmDelete = async () => {
    const { companyId } = deleteConfirm
    
    try {
      await deleteCompanyProspect(companyId)
      setDeleteConfirm({ show: false, companyId: null, companyName: '' })
      alert('✅ Company deleted successfully')
    } catch (error) {
      console.error('Error deleting company:', error)
      alert(`❌ Failed to delete company: ${error.message || 'Unknown error'}`)
      setDeleteConfirm({ show: false, companyId: null, companyName: '' })
    }
  }

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, companyId: null, companyName: '' })
  }

  const handleRemoveTag = async (companyId, tagToRemove) => {
    try {
      const company = companies.find(c => c.id === companyId)
      if (!company) return

      const currentTags = company.tags || []
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove)
      await updateCompanyProspect(companyId, { tags: updatedTags })
    } catch (error) {
      console.error('Error removing tag:', error)
      alert('Failed to remove tag. Please try again.')
    }
  }

  const handleAddTag = async (companyId, tagToAdd) => {
    try {
      const company = companies.find(c => c.id === companyId)
      if (!company) return

      const currentTags = company.tags || []
      if (!currentTags.includes(tagToAdd)) {
        const updatedTags = [...currentTags, tagToAdd]
        await updateCompanyProspect(companyId, { tags: updatedTags })
      }
    } catch (error) {
      console.error('Error adding tag:', error)
      alert('Failed to add tag. Please try again.')
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-'
    
    let date
    if (timestamp.toDate) {
      date = timestamp.toDate()
    } else if (timestamp instanceof Date) {
      date = timestamp
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000)
    } else {
      return '-'
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Filter companies
  const filteredCompanies = companies.filter((company) => {
    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = !searchTerm || (
      company.name?.toLowerCase().includes(searchLower) ||
      company.industry?.toLowerCase().includes(searchLower) ||
      company.country?.toLowerCase().includes(searchLower) ||
      company.employeeSize?.toLowerCase().includes(searchLower)
    )
    
    const matchesTag = !selectedTagFilter || (company.tags || []).includes(selectedTagFilter)
    
    return matchesSearch && matchesTag
  })

  // Sort function
  const sortCompanies = (companiesToSort, column, direction) => {
    if (!column) return companiesToSort

    return [...companiesToSort].sort((a, b) => {
      let aValue = a[column]
      let bValue = b[column]

      if (column === 'createdAt' || column === 'updatedAt') {
        const getDate = (timestamp) => {
          if (!timestamp) return new Date(0)
          if (timestamp.toDate) return timestamp.toDate()
          if (timestamp instanceof Date) return timestamp
          if (timestamp.seconds) return new Date(timestamp.seconds * 1000)
          return new Date(0)
        }
        aValue = getDate(aValue)
        bValue = getDate(bValue)
      } else if (column === 'tags') {
        const aTags = aValue || []
        const bTags = bValue || []
        if (aTags.length !== bTags.length) {
          return aTags.length - bTags.length
        }
        aValue = aTags.length > 0 ? aTags[0] : ''
        bValue = bTags.length > 0 ? bTags[0] : ''
      } else {
        aValue = aValue || ''
        bValue = bValue || ''
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      let comparison = 0
      if (aValue < bValue) {
        comparison = -1
      } else if (aValue > bValue) {
        comparison = 1
      }

      return direction === 'asc' ? comparison : -comparison
    })
  }

  const sortedCompanies = sortCompanies(filteredCompanies, sortColumn, sortDirection)

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  if (loading) {
    return <div className="loading">Loading companies...</div>
  }

  return (
    <div className="company-prospect-research-page">
      <div className="page-header">
        <div>
          <h2>Company List Building (Target Accounts)</h2>
          <p className="subtitle">Recommended: 1-2 hours per batch, 20 to 25 companies per batch • {companies.length} total companies</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New Company
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by company name, industry, country, or employee size..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="tag-filter">
          <select
            value={selectedTagFilter}
            onChange={(e) => setSelectedTagFilter(e.target.value)}
            className="tag-filter-select"
          >
            <option value="">All Tags</option>
            {tags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
        </div>
      </div>

      {showForm && (
        <CompanyForm
          onSubmit={editingCompany ? handleUpdateCompany : handleCreateCompany}
          onCancel={() => {
            setShowForm(false)
            setEditingCompany(null)
          }}
          tags={tags}
          initialData={editingCompany}
        />
      )}

      {sortedCompanies.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm || selectedTagFilter ? 'No companies match your filters.' : 'No companies yet. Add your first company!'}</p>
        </div>
      ) : (
        <div className="companies-table-container">
          <table className="companies-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('name')}>
                  <span>Company Name</span>
                  {sortColumn === 'name' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('industry')}>
                  <span>Industry</span>
                  {sortColumn === 'industry' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('employeeSize')}>
                  <span>Employee Size</span>
                  {sortColumn === 'employeeSize' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('country')}>
                  <span>Country</span>
                  {sortColumn === 'country' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Website</th>
                <th className="sortable" onClick={() => handleSort('tags')}>
                  <span>Tags</span>
                  {sortColumn === 'tags' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="sortable" onClick={() => handleSort('createdAt')}>
                  <span>Created</span>
                  {sortColumn === 'createdAt' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCompanies.map((company) => (
                <tr key={company.id}>
                  <td className="company-name-cell">
                    {company.name || '-'}
                  </td>
                  <td>{company.industry || '-'}</td>
                  <td>{company.employeeSize || '-'}</td>
                  <td>{company.country || '-'}</td>
                  <td>
                    {company.website ? (
                      <a href={company.website} target="_blank" rel="noopener noreferrer">
                        Visit Website
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="company-tags-cell">
                    <div className="company-tags">
                      {(company.tags || []).map((tag, idx) => (
                        <span key={idx} className="tag">
                          {tag}
                          <button
                            className="tag-remove"
                            onClick={() => handleRemoveTag(company.id, tag)}
                            title="Remove tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <select
                      className="tag-add-select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddTag(company.id, e.target.value)
                          e.target.value = ''
                        }
                      }}
                    >
                      <option value="">+ Add Tag</option>
                      {tags
                        .filter(tag => !(company.tags || []).includes(tag))
                        .map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                    </select>
                  </td>
                  <td title={formatDate(company.createdAt)}>
                    {formatDate(company.createdAt)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleEditCompany(company)
                        }}
                        title="Edit company"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={(e) => handleDeleteCompany(company.id, e)}
                        title="Delete company"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete Company</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteConfirm.companyName}</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                Delete Company
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Company Form Component
const CompanyForm = ({ onSubmit, onCancel, tags, initialData = null }) => {
  const industries = [
    'Technology',
    'Finance',
    'Healthcare',
    'Government',
    'Fintech',
    'SaaS',
    'Banking',
    'Insurance',
    'Manufacturing',
    'Retail',
    'Education',
    'Energy',
    'Telecommunications',
    'Real Estate',
    'Transportation',
    'Hospitality',
    'Media & Entertainment',
    'Consulting',
    'Legal',
    'Pharmaceuticals',
    'Biotechnology',
    'Aerospace',
    'Automotive',
    'Construction',
    'Agriculture',
    'Food & Beverage',
    'Fashion & Apparel',
    'Non-profit',
    'Security',
    'Cybersecurity',
    'AI/ML',
    'Cloud Services',
    'E-commerce',
    'Logistics',
    'Travel & Tourism'
  ]

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    industry: initialData?.industry || '',
    employeeSize: initialData?.employeeSize || '',
    country: initialData?.country || '',
    website: initialData?.website || '',
    tags: initialData?.tags || []
  })
  const [tagInput, setTagInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [industryFilter, setIndustryFilter] = useState(initialData?.industry || '')
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleIndustryChange = (e) => {
    const value = e.target.value
    setIndustryFilter(value)
    setFormData({
      ...formData,
      industry: value
    })
    setShowIndustryDropdown(true)
  }

  const handleIndustrySelect = (industry) => {
    setFormData({
      ...formData,
      industry: industry
    })
    setIndustryFilter(industry)
    setShowIndustryDropdown(false)
  }

  const filteredIndustries = industries.filter(industry =>
    industry.toLowerCase().includes(industryFilter.toLowerCase())
  )

  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tag]
      })
    }
    setTagInput('')
  }

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    })
  }

  return (
    <div className="form-overlay">
      <div className="form-modal">
        <div className="form-header">
          <h3>{initialData ? 'Edit Company' : 'New Company'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Company Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Acme Inc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="industry">Industry *</label>
            <div className="searchable-dropdown">
              <input
                type="text"
                id="industry"
                name="industry"
                value={industryFilter}
                onChange={handleIndustryChange}
                onFocus={() => setShowIndustryDropdown(true)}
                onBlur={() => {
                  // Delay to allow click on dropdown item
                  setTimeout(() => setShowIndustryDropdown(false), 200)
                }}
                required
                placeholder="Type to search industries..."
                autoComplete="off"
              />
              {showIndustryDropdown && filteredIndustries.length > 0 && (
                <div className="dropdown-list">
                  {filteredIndustries.map((industry, idx) => (
                    <div
                      key={idx}
                      className="dropdown-item"
                      onClick={() => handleIndustrySelect(industry)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {industry}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="employeeSize">Employee Size *</label>
            <input
              type="text"
              id="employeeSize"
              name="employeeSize"
              value={formData.employeeSize}
              onChange={handleChange}
              required
              placeholder="1-10, 11-50, 51-200, 201-500, 501-1000, 1001-5000, 5001-10000, 10000+"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              placeholder="United States"
            />
          </div>

          <div className="form-group">
            <label htmlFor="website">Website *</label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              required
              placeholder="https://www.example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <div className="tag-selector">
              <select
                value={tagInput}
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddTag(e.target.value)
                  }
                }}
                className="tag-select"
              >
                <option value="">Select a tag...</option>
                {tags
                  .filter(tag => !formData.tags.includes(tag))
                  .map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
              </select>
              {formData.tags.length > 0 && (
                <div className="selected-tags">
                  {formData.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                      <button
                        type="button"
                        className="tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="form-help-text">Add tags to filter companies by product or category</p>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (initialData ? 'Update Company' : 'Create Company')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CompanyProspectResearch
